import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMapGeometry } from "../../services/PlayerAreaService";
import { MapGeometryDto, AreaGeometryDto } from "../../models/PlayerArea";
import { mapConfigs } from "../../content/maps/mapData";
import { WrappedAreaDensity } from "../../services/WrappedService";
import styles from "./Wrapped.module.css";

/**
 * "Where you lived" — an isometric 3D render of the season's dwell time.
 *
 * Each map area becomes an extruded prism: footprint = the area polygon, height = time
 * spent there. This is FACEIT's kill-density showpiece adapted to the data we hold — we
 * store per-area dwell seconds (player_round_area_times), not per-kill coordinates.
 *
 * Geometry comes from /maps/{map}/geometry and is projected world -> radar (mapConfigs,
 * shared with PlayerAreaMap) -> isometric screen space.
 */

const W = 1100;
const H = 820;
const MAX_LIFT = 190; // px of extrusion for the busiest area

/** Cool slate (quiet) -> the site orange -> hot red. */
const ramp = (t: number): [number, number, number] => {
  const stops: Array<[number, [number, number, number]]> = [
    [0.0, [72, 80, 96]],
    [0.3, [140, 88, 62]],
    [0.62, [241, 92, 37]],
    [0.85, [255, 153, 51]],
    [1.0, [255, 214, 120]],
  ];
  for (let i = 0; i < stops.length - 1; i++) {
    const [p0, c0] = stops[i];
    const [p1, c1] = stops[i + 1];
    if (t <= p1) {
      const k = (t - p0) / (p1 - p0);
      return [
        Math.round(c0[0] + (c1[0] - c0[0]) * k),
        Math.round(c0[1] + (c1[1] - c0[1]) * k),
        Math.round(c0[2] + (c1[2] - c0[2]) * k),
      ];
    }
  }
  return stops[stops.length - 1][1];
};

const rgb = (c: [number, number, number], f: number, a = 1) =>
  `rgba(${Math.round(c[0] * f)},${Math.round(c[1] * f)},${Math.round(c[2] * f)},${a})`;

type Pt = { x: number; y: number };

/** Shoelace signed area — used to force a consistent winding so side walls face out. */
const signedArea = (pts: Pt[]) => {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    s += a.x * b.y - b.x * a.y;
  }
  return s / 2;
};

const centroid = (pts: Pt[]): Pt => {
  const c = pts.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: c.x / pts.length, y: c.y / pts.length };
};

const WrappedAreaMap = ({ density }: { density: WrappedAreaDensity }) => {
  const { t } = useTranslation("pages");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [geometry, setGeometry] = useState<MapGeometryDto | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setGeometry(null);
    setFailed(false);
    getMapGeometry(density.mapName)
      .then((g) => { if (!cancelled) { if (g) setGeometry(g); else setFailed(true); } })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [density.mapName]);

  const secondsByArea = useMemo(() => {
    const m = new Map<string, number>();
    density.areas.forEach((a) => m.set(a.areaName, a.seconds));
    return m;
  }, [density]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const config = mapConfigs[density.mapName];
    if (!canvas || !geometry || !config) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const peak = density.areas.reduce((mx, a) => Math.max(mx, a.seconds), 0);
    if (peak <= 0) return;

    // world -> radar space (0..1024), shared with PlayerAreaMap
    const toRadar = (wx: number, wy: number): Pt => ({
      x: (wx - config.pos_x) / config.scale,
      y: (config.pos_y - wy) / config.scale,
    });

    // radar -> isometric. Classic 2:1 dimetric so the map reads as a plate in 3D.
    const ISO = 0.56;
    const toIso = (p: Pt): Pt => ({ x: (p.x - p.y) * 0.86, y: (p.x + p.y) * ISO * 0.86 });

    const footprint = (area: AreaGeometryDto): Pt[] | null => {
      if (area.type === "polygon" && area.points && area.points.length > 2) {
        return area.points.map((pt) => toRadar(pt.x, pt.y));
      }
      if (area.minX != null && area.maxX != null && area.minY != null && area.maxY != null) {
        return [
          toRadar(area.minX, area.maxY),
          toRadar(area.maxX, area.maxY),
          toRadar(area.maxX, area.minY),
          toRadar(area.minX, area.minY),
        ];
      }
      return null;
    };

    // Build every prism first so we can centre the whole model and depth-sort it.
    const prisms = geometry.areas
      .map((area) => {
        const fp = footprint(area);
        if (!fp) return null;
        const seconds = secondsByArea.get(area.areaName) ?? 0;
        let iso = fp.map(toIso);
        // consistent CCW winding -> predictable outward-facing walls
        if (signedArea(iso) < 0) iso = iso.reverse();
        const c = centroid(iso);
        const intensity = seconds > 0 ? Math.sqrt(seconds / peak) : 0;
        return { area, seconds, iso, c, intensity, lift: intensity * MAX_LIFT };
      })
      .filter((p): p is NonNullable<typeof p> => p !== null);

    if (prisms.length === 0) return;

    // Fit the model to the canvas.
    const xs = prisms.flatMap((p) => p.iso.map((q) => q.x));
    const ys = prisms.flatMap((p) => p.iso.map((q) => q.y));
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const pad = 70;
    const scale = Math.min((W - pad * 2) / (maxX - minX), (H - pad * 2 - MAX_LIFT) / (maxY - minY));
    const offX = (W - (maxX - minX) * scale) / 2 - minX * scale;
    const offY = (H - (maxY - minY) * scale) / 2 - minY * scale + MAX_LIFT * 0.42;

    const place = (p: Pt, lift = 0): Pt => ({
      x: p.x * scale + offX,
      y: p.y * scale + offY - lift,
    });

    ctx.clearRect(0, 0, W, H);

    // Painter's algorithm: far (small x+y in iso terms) first.
    prisms.sort((a, b) => a.c.y - b.c.y || a.c.x - b.c.x);

    prisms.forEach(({ iso, intensity, lift, seconds }) => {
      const col = ramp(intensity);
      const base = iso.map((p) => place(p, 0));
      const top = iso.map((p) => place(p, lift));

      // --- ground footprint: always drawn, so unvisited areas still show the map shape
      ctx.beginPath();
      base.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
      ctx.fillStyle = seconds > 0 ? "rgba(255,255,255,0.045)" : "rgba(255,255,255,0.022)";
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.07)";
      ctx.lineWidth = 1;
      ctx.stroke();

      if (seconds <= 0 || lift < 1) return;

      // --- side walls, only the edges facing the viewer (screen-space normal pointing down)
      for (let i = 0; i < iso.length; i++) {
        const a = base[i];
        const b = base[(i + 1) % base.length];
        const at = top[i];
        const bt = top[(i + 1) % top.length];

        // outward normal of edge a->b in screen space; keep walls whose normal points
        // toward the camera (downward on screen)
        const nx = b.y - a.y;
        const ny = -(b.x - a.x);
        if (ny <= 0) continue;

        // light the two visible orientations differently so the solid reads as 3D
        const lit = nx > 0 ? 0.62 : 0.44;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.lineTo(bt.x, bt.y);
        ctx.lineTo(at.x, at.y);
        ctx.closePath();
        ctx.fillStyle = rgb(col, lit);
        ctx.fill();
        ctx.strokeStyle = rgb(col, lit * 0.75);
        ctx.lineWidth = 0.6;
        ctx.stroke();
      }

      // --- top face
      ctx.beginPath();
      top.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
      if (intensity > 0.7) {
        ctx.save();
        ctx.shadowColor = rgb(col, 1, 0.85);
        ctx.shadowBlur = 26;
        ctx.fillStyle = rgb(col, 1);
        ctx.fill();
        ctx.restore();
      }
      ctx.fillStyle = rgb(col, 1);
      ctx.fill();
      ctx.strokeStyle = rgb(col, 1.25);
      ctx.lineWidth = 1.1;
      ctx.stroke();
    });

    // Label the busiest few areas, drawn last so nothing occludes them.
    const labelled = [...prisms].filter((p) => p.seconds > 0)
      .sort((a, b) => b.seconds - a.seconds).slice(0, 3);
    labelled.forEach(({ area, c, lift }) => {
      const p = place(c, lift + 16);
      ctx.font = "700 19px system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.textAlign = "center";
      ctx.lineWidth = 4;
      ctx.strokeStyle = "rgba(0,0,0,0.75)";
      ctx.strokeText(area.areaName, p.x, p.y);
      ctx.fillStyle = "#fff";
      ctx.fillText(area.areaName, p.x, p.y);
    });
  }, [geometry, secondsByArea, density]);

  const top = density.areas[0];
  const minutes = Math.round(density.totalSeconds / 60);

  // No config for this map (e.g. nuke_lower) — skip rather than draw an unanchored blob.
  if (failed || !mapConfigs[density.mapName]) return null;

  return (
    <div className={styles.areaMapWrap}>
      <div className={styles.areaMapStage}>
        <canvas ref={canvasRef} width={W} height={H} className={styles.areaMapCanvas}
          role="img" aria-label={t("wrapped.areaMap.title", { map: density.mapName })} />
      </div>
      <div className={styles.areaMapLegend}>
        <span className={styles.areaMapMinutes}>{t("wrapped.areaMap.minutes", { minutes })}</span>
        {top && (
          <span className={styles.areaMapTop}>
            {t("wrapped.areaMap.favourite", { area: top.areaName })}
          </span>
        )}
      </div>
    </div>
  );
};

export default WrappedAreaMap;
